import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, TrendingUp, TrendingDown, Building2, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AnalyticsRow {
  sciId: string;
  sciName: string;
  propertyId: string;
  propertyAddress: string;
  propertyType: string;
  tenantName: string;
  rentAmount: number;
  chargesAmount: number;
  totalIncome: number;
  remainingCredit: number;
  monthlyPayment: number;
  creditEndDate: string | null;
  creditMonthsRemaining: number;
  balance: number;
}

interface SCISummary {
  sciId: string;
  sciName: string;
  totalIncome: number;
  totalCredit: number;
  totalMonthlyPayment: number;
  netBalance: number;
  propertiesCount: number;
}

export default function Analytics() {
  const [selectedSci, setSelectedSci] = useState<string>('all');

  // Fetch SCIs
  const { data: scis = [] } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch complete analytics data
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics', selectedSci],
    queryFn: async () => {
      // Fetch properties with mortgages
      const { data: properties, error: propError } = await supabase
        .from('properties')
        .select(`
          *,
          scis!inner(id, name),
          property_mortgages(*)
        `)
        .order('address');
      if (propError) throw propError;

      // Fetch active leases with tenants
      const { data: leases, error: leaseError } = await supabase
        .from('leases')
        .select(`
          *,
          tenants!inner(id, first_name, last_name, company_name, tenant_type),
          properties!inner(id, address, type, sci_id)
        `)
        .eq('status', 'active');
      if (leaseError) throw leaseError;

      // Build analytics rows
      const rows: AnalyticsRow[] = [];
      const today = new Date();

      for (const property of properties || []) {
        const sci = property.scis;
        
        // Filter by SCI if selected
        if (selectedSci !== 'all' && sci.id !== selectedSci) continue;

        // Find active lease for this property
        const lease = leases?.find(l => l.property_id === property.id);
        
        // Calculate mortgage info
        const activeMortgages = property.property_mortgages?.filter((m: any) => 
          !m.end_date || new Date(m.end_date) > today
        ) || [];
        
        const totalRemainingCredit = activeMortgages.reduce((sum: number, m: any) => sum + (m.remaining_amount || 0), 0);
        const totalMonthlyPayment = activeMortgages.reduce((sum: number, m: any) => sum + (m.monthly_payment || 0), 0);
        
        // Find the latest credit end date
        let creditEndDate: string | null = null;
        let creditMonthsRemaining = 0;
        if (activeMortgages.length > 0) {
          const latestMortgage = activeMortgages.reduce((latest: any, m: any) => {
            if (!m.end_date) return latest;
            if (!latest.end_date) return m;
            return new Date(m.end_date) > new Date(latest.end_date) ? m : latest;
          }, activeMortgages[0]);
          
          if (latestMortgage.end_date) {
            creditEndDate = latestMortgage.end_date;
            const endDate = new Date(latestMortgage.end_date);
            creditMonthsRemaining = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30)));
          }
        }

        const rentAmount = lease?.rent_amount || 0;
        const chargesAmount = lease?.charges_amount || 0;
        const totalIncome = rentAmount + chargesAmount;
        
        let tenantName = '-';
        if (lease?.tenants) {
          const tenant = lease.tenants;
          if (tenant.tenant_type === 'company' && tenant.company_name) {
            tenantName = tenant.company_name;
          } else {
            tenantName = [tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || '-';
          }
        }

        rows.push({
          sciId: sci.id,
          sciName: sci.name,
          propertyId: property.id,
          propertyAddress: property.address,
          propertyType: property.type || 'autre',
          tenantName,
          rentAmount,
          chargesAmount,
          totalIncome,
          remainingCredit: totalRemainingCredit,
          monthlyPayment: totalMonthlyPayment,
          creditEndDate,
          creditMonthsRemaining,
          balance: totalIncome - totalMonthlyPayment,
        });
      }

      // Calculate SCI summaries
      const sciSummaries: SCISummary[] = [];
      const sciGroups = rows.reduce((acc, row) => {
        if (!acc[row.sciId]) {
          acc[row.sciId] = {
            sciId: row.sciId,
            sciName: row.sciName,
            rows: [],
          };
        }
        acc[row.sciId].rows.push(row);
        return acc;
      }, {} as Record<string, { sciId: string; sciName: string; rows: AnalyticsRow[] }>);

      for (const group of Object.values(sciGroups)) {
        const totalIncome = group.rows.reduce((sum, r) => sum + r.totalIncome, 0);
        const totalCredit = group.rows.reduce((sum, r) => sum + r.remainingCredit, 0);
        const totalMonthlyPayment = group.rows.reduce((sum, r) => sum + r.monthlyPayment, 0);
        
        sciSummaries.push({
          sciId: group.sciId,
          sciName: group.sciName,
          totalIncome,
          totalCredit,
          totalMonthlyPayment,
          netBalance: totalIncome - totalMonthlyPayment,
          propertiesCount: group.rows.length,
        });
      }

      return { rows, summaries: sciSummaries };
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatPropertyType = (type: string) => {
    const types: Record<string, string> = {
      appartement: 'Appartement',
      maison: 'Maison',
      local_commercial: 'Local commercial',
      bureau: 'Bureau',
      entrepot: 'Entrepôt',
      immeuble: 'Immeuble',
      zone_industrielle: 'Zone industrielle',
      autre: 'Autre',
    };
    return types[type] || type;
  };

  const exportToCSV = () => {
    if (!analyticsData?.rows.length) return;

    const headers = [
      'SCI',
      'Adresse',
      'Type de bien',
      'Locataire',
      'Loyer',
      'Charges',
      'Total revenus',
      'Crédit restant',
      'Échéance mensuelle',
      'Date fin crédit',
      'Mois restants',
      'Balance',
    ];

    const csvRows = analyticsData.rows.map(row => [
      row.sciName,
      row.propertyAddress,
      formatPropertyType(row.propertyType),
      row.tenantName,
      row.rentAmount,
      row.chargesAmount,
      row.totalIncome,
      row.remainingCredit,
      row.monthlyPayment,
      row.creditEndDate ? format(new Date(row.creditEndDate), 'dd/MM/yyyy') : '-',
      row.creditMonthsRemaining,
      row.balance,
    ]);

    const csvContent = [
      headers.join(';'),
      ...csvRows.map(row => row.join(';')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `synthese-patrimoniale-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const totalBalance = analyticsData?.summaries.reduce((sum, s) => sum + s.netBalance, 0) || 0;
  const totalPatrimoine = analyticsData?.summaries.reduce((sum, s) => sum + s.totalCredit, 0) || 0;
  const totalRevenus = analyticsData?.summaries.reduce((sum, s) => sum + s.totalIncome, 0) || 0;

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title="Synthèse Patrimoniale"
          description="Tableau analytique complet de votre patrimoine immobilier"
        />

        {/* KPIs */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-rose-700">Revenus mensuels</CardTitle>
              <TrendingUp className="h-4 w-4 text-rose-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-rose-900">{formatCurrency(totalRevenus)}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-violet-700">Encours crédit total</CardTitle>
              <Building2 className="h-4 w-4 text-violet-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-violet-900">{formatCurrency(totalPatrimoine)}</p>
            </CardContent>
          </Card>

          <Card className={`bg-gradient-to-br ${totalBalance >= 0 ? 'from-emerald-50 to-emerald-100 border-emerald-200' : 'from-red-50 to-red-100 border-red-200'}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-sm font-medium ${totalBalance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                Marge nette mensuelle
              </CardTitle>
              {totalBalance >= 0 ? (
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${totalBalance >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                {formatCurrency(totalBalance)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Export */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <Select value={selectedSci} onValueChange={setSelectedSci}>
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Toutes les SCI" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les SCI</SelectItem>
              {scis.map((sci) => (
                <SelectItem key={sci.id} value={sci.id}>
                  {sci.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={exportToCSV} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
            <Download className="mr-2 h-4 w-4" />
            Exporter CSV
          </Button>
        </div>

        {/* Summary by SCI */}
        <Card className="border-violet-200">
          <CardHeader className="bg-gradient-to-r from-violet-50 to-rose-50">
            <CardTitle className="flex items-center gap-2 text-violet-900">
              <FileSpreadsheet className="h-5 w-5" />
              Synthèse par SCI
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-violet-50/50">
                  <TableHead className="font-semibold text-violet-900">SCI</TableHead>
                  <TableHead className="text-right font-semibold text-violet-900">Biens</TableHead>
                  <TableHead className="text-right font-semibold text-violet-900">Revenus</TableHead>
                  <TableHead className="text-right font-semibold text-violet-900">Crédit restant</TableHead>
                  <TableHead className="text-right font-semibold text-violet-900">Échéances</TableHead>
                  <TableHead className="text-right font-semibold text-violet-900">Marge nette</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analyticsData?.summaries.map((summary) => (
                  <TableRow key={summary.sciId} className="hover:bg-violet-50/30">
                    <TableCell className="font-medium">{summary.sciName}</TableCell>
                    <TableCell className="text-right">{summary.propertiesCount}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-medium">
                      {formatCurrency(summary.totalIncome)}
                    </TableCell>
                    <TableCell className="text-right text-violet-600">
                      {formatCurrency(summary.totalCredit)}
                    </TableCell>
                    <TableCell className="text-right text-rose-600">
                      {formatCurrency(summary.totalMonthlyPayment)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={summary.netBalance >= 0 ? 'default' : 'destructive'} className={summary.netBalance >= 0 ? 'bg-emerald-100 text-emerald-700' : ''}>
                        {formatCurrency(summary.netBalance)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Detailed Table */}
        <Card className="border-rose-200">
          <CardHeader className="bg-gradient-to-r from-rose-50 to-violet-50">
            <CardTitle className="text-rose-900">Détail par bien</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-rose-50/50">
                    <TableHead className="font-semibold text-rose-900">SCI</TableHead>
                    <TableHead className="font-semibold text-rose-900">Adresse</TableHead>
                    <TableHead className="font-semibold text-rose-900">Type</TableHead>
                    <TableHead className="font-semibold text-rose-900">Locataire</TableHead>
                    <TableHead className="text-right font-semibold text-rose-900">Loyer</TableHead>
                    <TableHead className="text-right font-semibold text-rose-900">Charges</TableHead>
                    <TableHead className="text-right font-semibold text-rose-900">Crédit restant</TableHead>
                    <TableHead className="text-right font-semibold text-rose-900">Échéance</TableHead>
                    <TableHead className="text-center font-semibold text-rose-900">Fin crédit</TableHead>
                    <TableHead className="text-right font-semibold text-rose-900">Mois rest.</TableHead>
                    <TableHead className="text-right font-semibold text-rose-900">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : analyticsData?.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                        Aucun bien trouvé
                      </TableCell>
                    </TableRow>
                  ) : (
                    analyticsData?.rows.map((row) => (
                      <TableRow key={row.propertyId} className="hover:bg-rose-50/30">
                        <TableCell className="font-medium">{row.sciName}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={row.propertyAddress}>
                          {row.propertyAddress}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {formatPropertyType(row.propertyType)}
                          </Badge>
                        </TableCell>
                        <TableCell>{row.tenantName}</TableCell>
                        <TableCell className="text-right text-emerald-600">
                          {formatCurrency(row.rentAmount)}
                        </TableCell>
                        <TableCell className="text-right text-blue-600">
                          {formatCurrency(row.chargesAmount)}
                        </TableCell>
                        <TableCell className="text-right text-violet-600">
                          {formatCurrency(row.remainingCredit)}
                        </TableCell>
                        <TableCell className="text-right text-rose-600">
                          {formatCurrency(row.monthlyPayment)}
                        </TableCell>
                        <TableCell className="text-center">
                          {row.creditEndDate 
                            ? format(new Date(row.creditEndDate), 'MMM yyyy', { locale: fr })
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.creditMonthsRemaining > 0 ? row.creditMonthsRemaining : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={row.balance >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                            {formatCurrency(row.balance)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
